import React from 'react';
import { Container, Typography, Box, Button, Grid, Card, CardContent } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  Event as EventIcon,
  QrCode2 as QrCodeIcon,
  People as PeopleIcon,
  TrendingUp as TrendingIcon,
  ArrowForward as ArrowIcon,
} from '@mui/icons-material';
import { motion } from 'framer-motion';

const FeatureCard = ({ icon, title, description, delay }) => (
  <motion.div
    initial={{ opacity: 1, y: 0 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.5, delay }}
    style={{ height: '100%', width: '100%' }}
  >
    <Card
      sx={{
        height: '100%',
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        textAlign: 'center',
        background: 'rgba(255, 255, 255, 0.7)',
        backdropFilter: 'blur(20px)',
        border: '1px solid',
        borderColor: 'rgba(255, 255, 255, 0.3)',
        borderRadius: 3,
        transition: 'all 0.3s ease',
        overflow: 'hidden',
        position: 'relative',
        '&:hover': {
          transform: 'translateY(-12px)',
          boxShadow: '0 16px 48px rgba(102, 126, 234, 0.2)',
          borderColor: 'rgba(102, 126, 234, 0.6)',
          background: 'rgba(255, 255, 255, 0.9)',
          '& .icon-circle': {
            transform: 'scale(1.1)',
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          },
        },
        '&:before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'radial-gradient(circle at top right, rgba(102, 126, 234, 0.05) 0%, transparent 60%)',
          pointerEvents: 'none',
        },
      }}
    >
      <CardContent sx={{ p: 5, display: 'flex', flexDirection: 'column', alignItems: 'center', flexGrow: 1 }}>
        <Box
          className="icon-circle"
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 80,
            height: 80,
            borderRadius: '50%',
            background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.2) 0%, rgba(118, 75, 162, 0.2) 100%)',
            color: 'primary.main',
            mb: 3,
            transition: 'all 0.3s ease',
          }}
        >
          {React.cloneElement(icon, { sx: { fontSize: 56, color: 'inherit' } })}
        </Box>
        <Typography
          variant="h5"
          gutterBottom
          fontWeight={700}
          sx={{
            mb: 2,
            fontSize: '1.5rem',
            lineHeight: 1.3,
          }}
        >
          {title}
        </Typography>
        <Typography
          variant="body1"
          color="text.secondary"
          sx={{
            lineHeight: 1.7,
            fontSize: '1rem',
          }}
        >
          {description}
        </Typography>
      </CardContent>
    </Card>
  </motion.div>
);

const Home = () => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();

  const features = [
    {
      icon: <EventIcon />,
      title: 'Event Management',
      description: 'Create and manage events with ease. Set capacity, deadlines, and track RSVPs in real-time.',
    },
    {
      icon: <QrCodeIcon />,
      title: 'QR Code Check-ins',
      description: 'Streamline event check-ins with QR codes. Fast, secure, and contactless attendance tracking.',
    },
    {
      icon: <PeopleIcon />,
      title: 'Attendee Management',
      description: 'Track attendees, manage guest lists, and export data for your records and analytics.',
    },
    {
      icon: <TrendingIcon />,
      title: 'Analytics & Reports',
      description: 'Gain insights into event performance with detailed analytics and exportable reports.',
    },
  ];

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#fafafa' }}>
      {/* Hero Section with Gradient */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white',
          py: { xs: 12, md: 16 },
          textAlign: 'center',
          position: 'relative',
          overflow: 'hidden',
          '&:before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'radial-gradient(circle at top right, rgba(255, 255, 255, 0.1) 0%, transparent 60%)',
            pointerEvents: 'none',
          },
        }}
      >
        <Container maxWidth="lg" sx={{ position: 'relative' }}>
          <motion.div
            initial={{ opacity: 1, y: 0 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <Box
              sx={{
                maxWidth: '900px',
                mx: 'auto',
                p: { xs: 3, md: 5 },
                borderRadius: 3,
                background: 'rgba(255, 255, 255, 0.1)',
                backdropFilter: 'blur(20px)',
                border: '1px solid rgba(255, 255, 255, 0.2)',
              }}
            >
              <Typography
                variant="h1"
                component="h1"
                gutterBottom
                sx={{
                  fontWeight: 800,
                  fontSize: { xs: '2.5rem', md: '4.5rem' },
                  lineHeight: 1.1,
                  letterSpacing: '-0.02em',
                  textShadow: '0 4px 20px rgba(0, 0, 0, 0.2)',
                  mb: 3,
                }}
              >
                Event Management Made Simple
              </Typography>
              <Typography
                variant="h5"
                paragraph
                sx={{
                  mb: 5,
                  opacity: 0.95,
                  fontSize: { xs: '1.125rem', md: '1.5rem' },
                  lineHeight: 1.6,
                  fontWeight: 400,
                  maxWidth: '700px',
                  mx: 'auto',
                }}
              >
                Manage events, track attendance, and streamline check-ins with QR codes
              </Typography>

              <Box
                sx={{
                  display: 'flex',
                  gap: 2,
                  justifyContent: 'center',
                  flexWrap: 'wrap',
                }}
              >
                {isAuthenticated ? (
                  <Button
                    variant="contained"
                    size="large"
                    endIcon={<ArrowIcon />}
                    onClick={() => navigate('/admin/dashboard')}
                    sx={{
                      bgcolor: 'white',
                      color: 'primary.main',
                      px: 5,
                      py: 2,
                      fontSize: '1.125rem',
                      fontWeight: 600,
                      borderRadius: 2,
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        bgcolor: 'grey.100',
                        transform: 'scale(1.05)',
                        boxShadow: '0 8px 24px rgba(0, 0, 0, 0.2)',
                      },
                    }}
                  >
                    Go to Dashboard
                  </Button>
                ) : (
                  <>
                    <Button
                      variant="contained"
                      size="large"
                      endIcon={<ArrowIcon />}
                      onClick={() => navigate('/events')}
                      sx={{
                        bgcolor: 'white',
                        color: 'primary.main',
                        px: 5,
                        py: 2,
                        fontSize: '1.125rem',
                        fontWeight: 600,
                        borderRadius: 2,
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          bgcolor: 'grey.100',
                          transform: 'scale(1.05)',
                          boxShadow: '0 8px 24px rgba(0, 0, 0, 0.2)',
                        },
                      }}
                    >
                      Browse Events
                    </Button>
                    <Button
                      variant="outlined"
                      size="large"
                      onClick={() => navigate('/login')}
                      sx={{
                        color: 'white',
                        borderColor: 'white',
                        px: 5,
                        py: 2,
                        fontSize: '1.125rem',
                        fontWeight: 600,
                        borderRadius: 2,
                        bgcolor: 'rgba(255, 255, 255, 0.1)',
                        backdropFilter: 'blur(10px)',
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          borderColor: 'white',
                          bgcolor: 'rgba(255, 255, 255, 0.2)',
                          transform: 'scale(1.05)',
                        },
                      }}
                    >
                      Sign In
                    </Button>
                  </>
                )}
              </Box>
            </Box>
          </motion.div>
        </Container>
      </Box>

      {/* Features Section with 2x2 Grid */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #fafafa 0%, #f5f5f5 100%)',
          py: { xs: 8, md: 12 },
        }}
      >
        <Container maxWidth="lg" sx={{ width: '100%' }}>
          <Box textAlign="center" mb={8}>
            <Typography
              variant="h2"
              gutterBottom
              sx={{
                fontWeight: 800,
                fontSize: { xs: '2rem', md: '3rem' },
                letterSpacing: '-0.02em',
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                mb: 2,
              }}
            >
              Everything You Need
            </Typography>
            <Typography
              variant="h6"
              color="text.secondary"
              sx={{
                fontSize: { xs: '1rem', md: '1.25rem' },
                lineHeight: 1.6,
                maxWidth: '600px',
                mx: 'auto',
                fontWeight: 400,
              }}
            >
              Powerful features to manage your events efficiently
            </Typography>
          </Box>

          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              flexWrap: 'wrap',
              gap: 4,
              width: '100%',
            }}
          >
            {features.map((feature, index) => (
              <Box
                key={index}
                sx={{
                  flex: { xs: '1 1 100%', sm: '1 1 calc(50% - 16px)', md: '1 1 calc(50% - 16px)' },
                  minWidth: { xs: '100%', sm: 'calc(50% - 16px)', md: 'calc(50% - 16px)' },
                  maxWidth: { xs: '100%', sm: 'calc(50% - 16px)', md: 'calc(50% - 16px)' },
                }}
              >
                <FeatureCard {...feature} delay={index * 0.1} />
              </Box>
            ))}
          </Box>
        </Container>
      </Box>

      {/* CTA Section with Glassmorphic Design */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #f0f0f0 0%, #e8e8e8 100%)',
          py: { xs: 8, md: 10 },
          textAlign: 'center',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <Container maxWidth="md">
          <Box
            sx={{
              p: { xs: 4, md: 6 },
              borderRadius: 3,
              background: 'rgba(255, 255, 255, 0.8)',
              backdropFilter: 'blur(20px)',
              border: '1px solid',
              borderColor: 'rgba(255, 255, 255, 0.5)',
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.08)',
            }}
          >
            <Typography
              variant="h3"
              gutterBottom
              sx={{
                fontWeight: 800,
                fontSize: { xs: '1.75rem', md: '2.5rem' },
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                mb: 2,
              }}
            >
              Ready to Get Started?
            </Typography>
            <Typography
              variant="body1"
              color="text.secondary"
              paragraph
              sx={{
                mb: 4,
                fontSize: { xs: '1rem', md: '1.125rem' },
                lineHeight: 1.7,
                maxWidth: '500px',
                mx: 'auto',
              }}
            >
              Join thousands of event organizers who trust our platform
            </Typography>
            <Button
              variant="contained"
              size="large"
              endIcon={<ArrowIcon />}
              onClick={() => navigate('/events')}
              sx={{
                px: 5,
                py: 2,
                fontSize: '1.125rem',
                fontWeight: 600,
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                borderRadius: 2,
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'scale(1.05)',
                  boxShadow: '0 12px 32px rgba(102, 126, 234, 0.3)',
                },
              }}
            >
              View Upcoming Events
            </Button>
          </Box>
        </Container>
      </Box>
    </Box>
  );
};

export default Home;
